import type { ReactNode } from 'react';

/** Shape of the entries produced by `SideMenuItems` (permission-filtered). */
export type NavItem = {
  key: string;
  label: string;
  icon?: ReactNode;
  /** Internal route. */
  path?: string;
  /** External link, opened in a new tab. */
  href?: string;
  onClick?: () => void;
  children?: NavItem[];
};

export const ACCOUNT_ITEM_KEY = 'user-information';
export const UPDATES_ITEM_KEY = 'updates';
export const SUPPORT_ITEM_KEY = 'report-issue';

const getItemPath = (item: NavItem) => item.path ?? item.key;

/**
 * Resolves which menu path is active for the current URL: an exact top-level match, otherwise the
 * longest parent path matching a top-level item or a group child, otherwise the first segment.
 */
export const getActivePath = (pathname: string, items: NavItem[]): string => {
  const topLevelPaths = items.map(getItemPath);

  if (topLevelPaths.includes(pathname)) {
    return pathname;
  }

  const segments = pathname.split('/').filter(Boolean);

  for (let index = segments.length; index > 0; index--) {
    const candidate = `/${segments.slice(0, index).join('/')}`;

    if (
      topLevelPaths.includes(candidate) ||
      items.some((item) => item.children?.some((child) => getItemPath(child) === candidate))
    ) {
      return candidate;
    }
  }

  return `/${segments[0] ?? ''}`;
};

export const isItemActive = (item: NavItem, activePath: string) =>
  Boolean(item.path) && item.path === activePath;
